// API del almacén de vídeos de análisis de movimiento. NO habla con el proxy
// PostgREST: apunta a /api/motion-storage (Edge Function), que firma URLs del
// bucket privado 'motion-analysis'. La subida real de bytes se hace con un PUT
// directo a la URL firmada (queryFn), fuera del Edge (límite de body de Vercel).
import { createApi } from '@reduxjs/toolkit/query/react';
import { makeAuthedBaseQuery } from '../baseQuery';

const motionStorageApi = createApi({
  reducerPath: 'motionStorage',
  tagTypes: ['MotionVideo'],
  baseQuery: makeAuthedBaseQuery('/api/motion-storage'),
  endpoints(builder) {
    return {
      fetchMotionVideos: builder.query({
        providesTags: (result, error, athleteId) => [{ type: 'MotionVideo', id: athleteId }],
        query: (athleteId) => ({
          url: '',
          method: 'POST',
          body: { action: 'list', athleteId },
        }),
      }),

      deleteMotionVideo: builder.mutation({
        invalidatesTags: (result, error, { athleteId }) => [
          { type: 'MotionVideo', id: athleteId },
        ],
        query: ({ athleteId, videoName }) => ({
          url: '',
          method: 'POST',
          body: { action: 'delete', athleteId, videoName },
        }),
      }),

      deleteMotionCorrection: builder.mutation({
        invalidatesTags: (result, error, { athleteId }) => [
          { type: 'MotionVideo', id: athleteId },
        ],
        query: ({ athleteId, correctionName }) => ({
          url: '',
          method: 'POST',
          body: { action: 'delete-correction', athleteId, correctionName },
        }),
      }),

      // Edita los metadatos de un vídeo: ejercicio, peso, repeticiones y %RM.
      // (Renombra el objeto en Storage: los metadatos viven en el nombre.)
      setMotionVideoMeta: builder.mutation({
        invalidatesTags: (result, error, { athleteId }) => [
          { type: 'MotionVideo', id: athleteId },
        ],
        query: ({ athleteId, videoName, exerciseId, weight, reps, rmPercent }) => ({
          url: '',
          method: 'POST',
          body: { action: 'set-meta', athleteId, videoName, exerciseId, weight, reps, rmPercent },
        }),
      }),

      // Sube un vídeo (kind:'video') o una corrección exportada
      // (kind:'correction' + sourceVideo + ext): pide URL firmada y hace el PUT.
      uploadMotionFile: builder.mutation({
        invalidatesTags: (result, error, { athleteId }) => [
          { type: 'MotionVideo', id: athleteId },
        ],
        async queryFn(
          { athleteId, kind, fileName, sourceVideo, ext, blob, contentType, meta },
          _api,
          _extraOptions,
          fetchWithBQ
        ) {
          const signRes = await fetchWithBQ({
            url: '',
            method: 'POST',
            body: { action: 'sign-upload', athleteId, kind, fileName, sourceVideo, ext },
          });
          if (signRes.error) return { error: signRes.error };
          const { uploadUrl, path } = signRes.data;
          try {
            const put = await fetch(uploadUrl, {
              method: 'PUT',
              headers: {
                'content-type': contentType || 'application/octet-stream',
                'x-upsert': 'true',
              },
              body: blob,
            });
            if (!put.ok) {
              return { error: { status: put.status, data: 'upload_failed' } };
            }
          } catch (err) {
            return { error: { status: 'FETCH_ERROR', data: String(err?.message || err) } };
          }
          // Los metadatos del vídeo viven en la tabla motion_video: se crea la
          // fila DESPUÉS de subir los bytes (si el PUT falla, no queda fila).
          if (kind === 'video') {
            const recorded = await fetchWithBQ({
              url: '',
              method: 'POST',
              body: { action: 'create-video', athleteId, fileName, ...(meta || {}) },
            });
            if (recorded.error) return { error: recorded.error };
          }
          return { data: { path } };
        },
      }),
    };
  },
});

export const {
  useFetchMotionVideosQuery,
  useDeleteMotionVideoMutation,
  useDeleteMotionCorrectionMutation,
  useSetMotionVideoMetaMutation,
  useUploadMotionFileMutation,
} = motionStorageApi;
export { motionStorageApi };
